import React, { useState } from 'react';
import { Drawer, List, Progress, Typography, Button, Tag, Checkbox } from 'antd';
import { useTask } from '../context/TaskContext';
import { Task, TaskStatus } from '../interfaces/Task';
import dayjs from 'dayjs';

const { Text } = Typography;

// CSS for slide-up animation
const slideUpStyle = {
  transition: 'transform 0.2s ease-in-out',
  transform: 'translateY(0)',
};

const slideUpHoverStyle = {
  transition: 'transform 0.2s ease-in-out',
  transform: 'translateY(-2px)',
};

const getStatusColor = (status: TaskStatus) => {
  switch (status) {
    case 'completed':
      return 'success';
    case 'failed':
      return 'error';
    case 'running':
      return 'processing';
    case 'cancelled':
      return 'error';
    default:
      return 'default';
  }
};

const getTaskDescription = (task: Task) => {
  let description = '';
  if (task.result) {
    if (typeof task.result === 'object') {
      if (
        task.result.data?.successCount !== undefined &&
        task.result.data?.totalCount !== undefined
      ) {
        description = `Successfully processed: ${task.result.data.successCount}/${task.result.data.totalCount} products`;
      } else if (task.result.data?.statusMessage) {
        description = task.result.data.statusMessage;
      } else if (task.result.totalProducts) {
        description = `Successfully processed ${task.result.totalProducts} products`;
      }
    } else if (typeof task.result === 'string') {
      description = task.result;
    }
  }

  return (
    <>
      <Text type="secondary">Started by: {task.userName}</Text>
      <div>
        <Text type="secondary">Started: {dayjs(task.createdAt).format('HH:mm:ss')}</Text>
        {task.completedAt && (
          <Text type="secondary" style={{ marginLeft: 8 }}>
            Completed: {dayjs(task.completedAt).format('HH:mm:ss')}
          </Text>
        )}
        {description && (
          <div style={{ margin: '8px 0' }}>
            <Text type={task.status === 'completed' ? 'success' : 'secondary'}>{description}</Text>
          </div>
        )}
      </div>
      {(task.status === 'running' || task.status === 'cancelled') && (
        <div>
          {task.progressMessage && (
            <div style={{ margin: '4px 0' }}>
              <Text>{task.progressMessage}</Text>
            </div>
          )}
          <Progress percent={Number(getTaskProgress(task))} size="small" />
        </div>
      )}
      {task.error && (
        <Text type="danger" style={{ display: 'block' }}>
          Error: {task.error}
        </Text>
      )}
    </>
  );
};

const getTaskProgress = (task: Task) => {
  return task.progress;
};

const TaskDrawer: React.FC = () => {
  const {
    tasks,
    isDrawerOpen,
    setIsDrawerOpen,
    selectedTask,
    setSelectedTask,
    clearCompletedTasks,
    socket,
    isAdmin,
    showMyTasksOnly,
    setShowMyTasksOnly,
    filteredTasks,
  } = useTask();

  const [hoveredTaskId, setHoveredTaskId] = useState<string | null>(null);

  const handleTaskClick = (task: Task) => {
    setSelectedTask(task);
  };

  const handleClearTasks = async () => {
    await clearCompletedTasks();
  };

  return (
    <Drawer
      title="Task Progress"
      placement="right"
      onClose={() => setIsDrawerOpen(false)}
      open={isDrawerOpen}
      width={400}
    >
      {isAdmin && (
        <div>
          <Checkbox checked={showMyTasksOnly} onChange={e => setShowMyTasksOnly(e.target.checked)}>
            Show my tasks only
          </Checkbox>
        </div>
      )}
      <List
        dataSource={filteredTasks}
        renderItem={task => (
          <List.Item
            style={{
              cursor:
                task.status === 'completed' ||
                task.status === 'failed' ||
                task.status === 'cancelled'
                  ? 'pointer'
                  : 'default',
            }}
            onClick={() => handleTaskClick(task)}
          >
            <List.Item.Meta
              title={
                <div
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Text strong>{task.type}</Text>
                  <div>
                    {task.status === 'running' ? (
                      hoveredTaskId === task.id ? (
                        <Button
                          type="link"
                          danger
                          size="small"
                          onClick={e => {
                            e.stopPropagation();
                            socket?.emit?.('cancelTask', task.id);
                          }}
                          style={{
                            border: '1px solid #ff4d4f',
                            ...slideUpStyle,
                          }}
                          onMouseEnter={e => {
                            Object.assign(e.currentTarget.style, slideUpHoverStyle);
                          }}
                          onMouseLeave={e => {
                            setHoveredTaskId(null);
                            Object.assign(e.currentTarget.style, slideUpStyle);
                          }}
                        >
                          Cancel Task
                        </Button>
                      ) : (
                        <Tag
                          color={getStatusColor(task.status)}
                          style={{
                            cursor: 'pointer',
                            ...slideUpStyle,
                          }}
                          onMouseEnter={e => {
                            setHoveredTaskId(task.id);
                            Object.assign(e.currentTarget.style, slideUpHoverStyle);
                          }}
                          onMouseLeave={e => {
                            setHoveredTaskId(null);
                            Object.assign(e.currentTarget.style, slideUpStyle);
                          }}
                        >
                          {task.status.toUpperCase()}
                        </Tag>
                      )
                    ) : (
                      <Tag color={getStatusColor(task.status)}>{task.status.toUpperCase()}</Tag>
                    )}
                  </div>
                </div>
              }
              description={getTaskDescription(task)}
            />
          </List.Item>
        )}
      />
      {filteredTasks.length === 0 && (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Text type="secondary">No tasks to display</Text>
        </div>
      )}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '10px 24px',
          borderTop: '1px solid #f0f0f0',
        }}
      >
        <Button type="primary" onClick={handleClearTasks} block>
          Clear
        </Button>
      </div>
    </Drawer>
  );
};

export default TaskDrawer;
